// First: before any module that builds a zod schema (see the module).
import './jitless.js';
import './styles/index.css';
// Listening for Chromium's install offer from the start, which can come before
// the banner that shows it has mounted (see the module).
import './install/installPrompt.js';

import { createRouter, RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { StaleTabGate } from './components/shell/StaleTabGate.js';
import { routeTree } from './routeTree.gen';
import { catchUpSecrets } from './secrets/catchUp.js';
import { tabLock } from './secrets/tabLock.js';
import { db } from './store/db.js';
import { makeWayWhenAsked, updateChannel } from './sync/beforeUpdate.js';
import { syncScheduler } from './sync/runtime.js';
import { keepAwakeWhileSyncing } from './sync/wakeLock.js';

const router = createRouter({
	routeTree,
	// The app renders from local state before any network call, so there is
	// nothing to wait on at boot.
	defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
	interface Register {
		router: typeof router;
	}
}

// Before the first render, and never stopped: it follows the connection on
// its own, and syncs nothing while there is none.
syncScheduler.start();
// The screen kept on through a long sync, so a phone's timeout does not stop
// an import part-way. Never stopped either, for the same reason.
keepAwakeWhileSyncing(syncScheduler);
// Another tab's Reload pauses syncing here too, so the new build can take over
// (`sync/beforeUpdate.ts`). Never stopped either.
makeWayWhenAsked(syncScheduler, updateChannel());
// Secrets still made with a key a source had before, encrypted afresh to the
// one it has as it is unlocked (`secrets/catchUp.ts`). Never stopped either.
catchUpSecrets({ db, lock: tabLock, scheduler: syncScheduler });

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('#root is missing from index.html');

createRoot(rootElement).render(
	<StrictMode>
		{/* Outside the router, so no route's error screen can take its place: once
		    the database has closed, every query under here fails. */}
		<StaleTabGate>
			<RouterProvider router={router} />
		</StaleTabGate>
	</StrictMode>
);
